import {
  AI_BUILDER_CLIENT_TIMEOUT_MS,
  AskAiBuilderResponse,
  GenerateFlowRequest,
  WebsocketClientEvent,
  WebsocketServerEvent,
} from '@activepieces/shared';
import { Socket } from 'socket.io-client';

import { sentry } from '@/lib/sentry';

/**
 * Resolves with the answer to one AI Builder turn. The socket is shared by
 * every turn and every tab, hence the requestId filter; the timeout only fires
 * when the server never answered at all, since it answers within its own budget.
 */
const waitForTurnResponse = (
  socket: Socket,
  requestId: string,
): Promise<AskAiBuilderResponse> =>
  new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      socket.off(WebsocketClientEvent.GENERATE_FLOW_FINISHED, onFinished);
      reject(new Error('Timeout waiting for generate flow response'));
    }, AI_BUILDER_CLIENT_TIMEOUT_MS);

    function onFinished(data: AskAiBuilderResponse) {
      if (data.requestId && data.requestId !== requestId) {
        return;
      }
      clearTimeout(timeout);
      socket.off(WebsocketClientEvent.GENERATE_FLOW_FINISHED, onFinished);
      resolve(data);
    }

    socket.on(WebsocketClientEvent.GENERATE_FLOW_FINISHED, onFinished);
  });

/**
 * Sends one AI Builder turn over the socket and waits for its answer. Pure
 * socket I/O: it never touches canvas state. The caller decides `requestId`
 * because it also stamps every mid-turn event of the same turn, so it must
 * exist before anything is sent.
 */
export const generateFlow = async (
  params: GenerateFlowRequest & { requestId: string },
  socket: Socket,
): Promise<AskAiBuilderResponse> => {
  socket.emit(WebsocketServerEvent.GENERATE_FLOW, params);

  try {
    return await waitForTurnResponse(socket, params.requestId);
  } catch (error) {
    sentry.captureException(error, 'AI flow generation failed', {
      tags: {
        feature: 'ai-builder',
        operation: 'generateFlow',
      },
      extra: {
        // The input's kind and length only: the user's text must never reach Sentry
        promptLength:
          params.input.type === 'text' ? params.input.text.length : 0,
        signalKind: params.input.type,
      },
    });
    throw error;
  }
};
